import React, { useEffect, useRef, useState } from "react";
import {
    Bell,
    Calendar,
    Check,
    Flag,
    Repeat,
    Send,
    Sparkles,
    User,
} from "lucide-react";
import { toast } from "react-toastify";
import { PRIORITY_OPTIONS, REPEAT_OPTIONS } from "../constants";
import { parseQuickAddInput, quickAddHintText } from "../utils/parseQuickAddInput";
import { appendReminderFields, scheduleTaskReminder } from "../utils/taskReminderSync";
import ReminderPicker from "./ReminderPicker";
import "../task-quick-add.css";

function toDateInputValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function resolveDueDate(preset, customDate) {
    if (preset === "tomorrow") {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d;
    }
    if (preset === "custom" && customDate) {
        return new Date(customDate);
    }
    return new Date();
}

function PopoverMenu({ items, selectedValue, onSelect, className = "" }) {
    return (
        <div className={`task-quick-add__popover ${className}`} role="listbox">
            {items.map((item) => (
                <button
                    key={item.value}
                    type="button"
                    role="option"
                    aria-selected={selectedValue === item.value}
                    onClick={() => onSelect(item)}
                    className={`task-quick-add__popover-item ${
                        selectedValue === item.value ? "task-quick-add__popover-item--selected" : ""
                    } ${item.className || ""}`}
                >
                    {item.label}
                </button>
            ))}
        </div>
    );
}

const TaskQuickAdd = ({
    role = "admin",
    apiUrl,
    token,
    assignOptions = [],
    onTaskCreated,
    defaultAssignee = null,
    inputRef: externalInputRef,
    variant = "inline",
}) => {
    const rootRef = useRef(null);
    const internalRef = useRef(null);
    const inputRef = externalInputRef || internalRef;
    const [title, setTitle] = useState("");
    const [duePreset, setDuePreset] = useState("today");
    const [customDueDate, setCustomDueDate] = useState(toDateInputValue(new Date()));
    const [assignTo, setAssignTo] = useState(defaultAssignee);
    const [priority, setPriority] = useState("medium");
    const [repetition, setRepetition] = useState("none");
    const [reminderPreset, setReminderPreset] = useState("none");
    const [reminderCustomMinutes, setReminderCustomMinutes] = useState(15);
    const [startTime, setStartTime] = useState("09:00");
    const [submitting, setSubmitting] = useState(false);
    const [openPanel, setOpenPanel] = useState(null);
    const [parsedPreview, setParsedPreview] = useState(null);
    const [focused, setFocused] = useState(false);
    const [justAdded, setJustAdded] = useState(false);

    const isDock = variant === "dock";
    const hasText = Boolean(title.trim());
    const isExpanded = isDock ? focused || hasText || Boolean(openPanel) : true;

    const handleTitleChange = (value) => {
        setTitle(value);
        const parsed = parseQuickAddInput(value);
        setParsedPreview(parsed.hadHints ? parsed : null);
    };

    useEffect(() => {
        if (defaultAssignee && !assignTo) setAssignTo(defaultAssignee);
    }, [defaultAssignee, assignTo]);

    useEffect(() => {
        if (assignOptions.length > 0 && !assignTo && role === "admin") {
            setAssignTo(assignOptions[0]);
        }
    }, [assignOptions, assignTo, role]);

    useEffect(() => {
        if (!openPanel) return undefined;
        const close = (e) => {
            if (!rootRef.current?.contains(e.target)) setOpenPanel(null);
        };
        document.addEventListener("mousedown", close);
        document.addEventListener("touchstart", close);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("touchstart", close);
        };
    }, [openPanel]);

    const resetForm = () => {
        setTitle("");
        setDuePreset("today");
        setCustomDueDate(toDateInputValue(new Date()));
        setPriority("medium");
        setRepetition("none");
        setReminderPreset("none");
        setStartTime("09:00");
        setOpenPanel(null);
        setParsedPreview(null);
        if (role === "admin" && assignOptions.length > 0) setAssignTo(assignOptions[0]);
    };

    const handleCreate = async () => {
        const raw = title.trim();
        if (!raw) {
            toast.error("Enter a task title");
            inputRef.current?.focus();
            return;
        }
        if (role === "admin" && assignOptions.length > 0 && !assignTo?.value) {
            toast.error("Select who to assign this task to");
            return;
        }

        const parsed = parseQuickAddInput(raw);
        const finalTitle = parsed.hadHints ? parsed.title.trim() : raw;
        if (!finalTitle) {
            toast.error(
                parsed.hadHints
                    ? "Add a task name (e.g. Restock bar tomorrow p1)"
                    : "Enter a task title"
            );
            inputRef.current?.focus();
            return;
        }
        const effectivePriority = parsed.hadHints ? parsed.priority : priority;
        const effectiveRepetition = parsed.hadHints && parsed.repetition !== "none" ? parsed.repetition : repetition;
        const effectiveDuePreset = parsed.hadHints ? parsed.duePreset : duePreset;
        const effectiveCustomDate = parsed.customDueDate || customDueDate;

        setSubmitting(true);
        try {
            const dueDate = resolveDueDate(effectiveDuePreset, effectiveCustomDate);
            const formData = new FormData();
            formData.append("title", finalTitle);
            formData.append("due_date", dueDate.toISOString());
            formData.append("start_date", new Date().toISOString());
            formData.append("priority", effectivePriority);
            formData.append("repetition", effectiveRepetition);
            formData.append("start_time", startTime || "09:00");
            appendReminderFields(formData, reminderPreset, reminderCustomMinutes);
            formData.append("notes", "");
            if (role === "admin" && assignTo?.value) {
                formData.append("assign_to", assignTo.value);
            }

            const endpoint =
                role === "admin" ? `${apiUrl}/task` : `${apiUrl}/task/create-task`;
            const response = await fetch(endpoint, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            const res = await response.json();
            if (response.ok) {
                setJustAdded(true);
                window.setTimeout(() => setJustAdded(false), 600);
                toast.success("Task added");
                resetForm();
                if (res.task) scheduleTaskReminder(res.task, role);
                onTaskCreated?.(res.task);
                inputRef.current?.focus();
            } else {
                toast.error(res.error || res.message || "Failed to create task");
            }
        } catch (error) {
            toast.error(error.message || "Failed to create task");
        } finally {
            setSubmitting(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleCreate();
        }
        if (e.key === "Escape") {
            setOpenPanel(null);
            inputRef.current?.blur();
        }
    };

    const togglePanel = (panel) => {
        setOpenPanel((current) => (current === panel ? null : panel));
    };

    const priorityLabelData = PRIORITY_OPTIONS.find((p) => p.value === priority);
    const repeatLabel = REPEAT_OPTIONS.find((r) => r.value === repetition)?.label;
    const reminderActive = reminderPreset !== "none";
    const repeatActive = repetition !== "none";
    const dueLabel =
        duePreset === "today"
            ? "Today"
            : duePreset === "tomorrow"
              ? "Tomorrow"
              : customDueDate
                ? new Date(`${customDueDate}T12:00:00`).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                  })
                : "Pick date";

    const reminderShort =
        reminderPreset === "none"
            ? "Reminder"
            : reminderPreset === "at_due"
              ? "At due"
              : reminderPreset === "custom"
                ? `${reminderCustomMinutes}m`
                : `${reminderPreset}m`;

    const assignShort = assignTo?.label?.split(" (")[0] || "Assign";

    const rootClass = [
        "task-quick-add",
        isDock ? "task-quick-add--dock" : "task-quick-add--inline",
        focused ? "task-quick-add--focused" : "",
        isExpanded ? "task-quick-add--expanded" : "",
        hasText ? "task-quick-add--has-text" : "",
        justAdded ? "task-quick-add--success" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div ref={rootRef} className={rootClass}>
            <div className="task-quick-add__inner">
                <div className="task-quick-add__input-row">
                    <div className="task-quick-add__status" aria-hidden />
                    <div className="task-quick-add__input-wrap">
                        <input
                            ref={inputRef}
                            type="text"
                            value={title}
                            onChange={(e) => handleTitleChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onFocus={() => setFocused(true)}
                            onBlur={() => window.setTimeout(() => setFocused(false), 120)}
                            placeholder={
                                isDock
                                    ? "What needs to be done?"
                                    : "What needs to be done? (try: tomorrow p1)"
                            }
                            disabled={submitting}
                            className="task-quick-add__input"
                            aria-label="New task title"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={submitting || !hasText}
                        className="task-quick-add__submit"
                        aria-label={submitting ? "Adding task" : "Add task"}
                    >
                        {submitting ? (
                            "…"
                        ) : justAdded ? (
                            <Check size={18} strokeWidth={2.5} />
                        ) : (
                            <>
                                <span className="task-quick-add__submit-label">Add</span>
                                <Send size={17} strokeWidth={2.25} />
                            </>
                        )}
                    </button>
                </div>

                {parsedPreview && (
                    <div className="task-quick-add__magic">
                        <Sparkles size={12} />
                        <span>
                            {parsedPreview.title}
                            {parsedPreview.duePreset !== "today" && ` · ${parsedPreview.duePreset}`}
                            {parsedPreview.priority !== "medium" && ` · ${parsedPreview.priority}`}
                            {parsedPreview.repetition !== "none" && ` · ${parsedPreview.repetition}`}
                        </span>
                    </div>
                )}

                <div className="task-quick-add__controls">
                    <div className="task-quick-add__controls-inner">
                        <div className="task-quick-add__segments" role="tablist" aria-label="Due date">
                            {[
                                { id: "today", label: "Today" },
                                { id: "tomorrow", label: "Tomorrow" },
                            ].map(({ id, label }) => (
                                <button
                                    key={id}
                                    type="button"
                                    role="tab"
                                    aria-selected={duePreset === id}
                                    onClick={() => {
                                        setDuePreset(id);
                                        setOpenPanel(null);
                                    }}
                                    className={`task-quick-add__segment ${
                                        duePreset === id ? "task-quick-add__segment--active" : ""
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                            <div className="relative flex-1">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={duePreset === "custom"}
                                    onClick={() => togglePanel("date")}
                                    className={`task-quick-add__segment task-quick-add__segment--date w-full ${
                                        duePreset === "custom" ? "task-quick-add__segment--active" : ""
                                    }`}
                                >
                                    <Calendar size={12} />
                                    {duePreset === "custom" ? dueLabel : "Date"}
                                </button>
                                {openPanel === "date" && (
                                    <div className="task-quick-add__popover task-quick-add__popover-panel">
                                        <input
                                            type="date"
                                            value={customDueDate}
                                            onChange={(e) => {
                                                setCustomDueDate(e.target.value);
                                                setDuePreset("custom");
                                            }}
                                            className="task-quick-add__date-input [color-scheme:dark]"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="task-quick-add__tools">
                            {role === "admin" && assignOptions.length > 0 && (
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => togglePanel("assign")}
                                        className={`task-quick-add__tool ${
                                            openPanel === "assign" ? "task-quick-add__tool--active" : ""
                                        }`}
                                    >
                                        <User size={13} />
                                        {assignShort}
                                    </button>
                                    {openPanel === "assign" && (
                                        <PopoverMenu
                                            items={assignOptions.map((opt) => ({
                                                value: opt.value,
                                                label: opt.label,
                                            }))}
                                            selectedValue={assignTo?.value}
                                            onSelect={(item) => {
                                                setAssignTo(
                                                    assignOptions.find((o) => o.value === item.value)
                                                );
                                                setOpenPanel(null);
                                            }}
                                        />
                                    )}
                                </div>
                            )}

                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => togglePanel("priority")}
                                    className={`task-quick-add__tool task-quick-add__tool--priority-${priority} ${
                                        openPanel === "priority" ? "task-quick-add__tool--active" : ""
                                    }`}
                                >
                                    <span className="task-quick-add__tool-dot" />
                                    <Flag size={13} />
                                    {priorityLabelData?.label}
                                </button>
                                {openPanel === "priority" && (
                                    <PopoverMenu
                                        items={PRIORITY_OPTIONS}
                                        selectedValue={priority}
                                        onSelect={(item) => {
                                            setPriority(item.value);
                                            setOpenPanel(null);
                                        }}
                                    />
                                )}
                            </div>

                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => togglePanel("repeat")}
                                    className={`task-quick-add__tool ${
                                        repeatActive || openPanel === "repeat"
                                            ? "task-quick-add__tool--active"
                                            : ""
                                    }`}
                                >
                                    <Repeat size={13} />
                                    {repeatActive ? repeatLabel : "Repeat"}
                                </button>
                                {openPanel === "repeat" && (
                                    <PopoverMenu
                                        items={REPEAT_OPTIONS}
                                        selectedValue={repetition}
                                        onSelect={(item) => {
                                            setRepetition(item.value);
                                            setOpenPanel(null);
                                        }}
                                    />
                                )}
                            </div>

                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => togglePanel("reminder")}
                                    className={`task-quick-add__tool ${
                                        reminderActive || openPanel === "reminder"
                                            ? "task-quick-add__tool--active"
                                            : ""
                                    }`}
                                >
                                    <Bell size={13} />
                                    {reminderShort}
                                </button>
                                {openPanel === "reminder" && (
                                    <div className="task-quick-add__popover task-quick-add__popover-panel">
                                        <ReminderPicker
                                            value={reminderPreset}
                                            customMinutes={reminderCustomMinutes}
                                            onChange={({ preset, customMinutes }) => {
                                                setReminderPreset(preset);
                                                if (customMinutes) setReminderCustomMinutes(customMinutes);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {!isDock && (
                    <p className="task-quick-add__hint">{quickAddHintText()}</p>
                )}
            </div>
        </div>
    );
};

export default TaskQuickAdd;
